import { Auth } from "aws-amplify";
import { Component, FunctionComponent } from "react";
import { Color, Path, Storage } from "../../env";
import { Alert, Form } from "react-bootstrap";
import { Button, variant } from "../form/Button";
import { IUpdateShopBrand, UpdateShopBrandOutput } from "../../client/core";
import { RouteComponentProps } from "react-router-dom";

// Mirrors the server side rule in EcomniCore setshopbrand. This supplements the
// API check rather than replacing it.
const hexColor = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const isValidHexColor = (color: string): boolean => hexColor.test(color);

// The palette offered alongside the free colour input.
export const presetColors = [
    Color.Primary,
    Color.Black,
    Color.Brown,
    Color.DarkGrey,
    Color.Grey,
    Color.White,
    Color.Red,
    Color.Orange,
];

// A native colour input only accepts a lowercase six digit hex code, so a three
// digit shorthand is expanded and anything unusable falls back to a neutral
// placeholder. The value the merchant submits is left untouched.
export const toPickerValue = (color: string): string => {
    if (!isValidHexColor(color)) {
        return Color.LightGrey.toLowerCase();
    }
    const hex = color.slice(1);
    if (hex.length === 3) {
        return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toLowerCase();
    }
    return color.toLowerCase();
};

export interface UpdateBrandState {
    color: string;
    error: string;
}

export interface UpdateBrandProps extends RouteComponentProps {
    auth: typeof Auth;
    updateBrandAPI: IUpdateShopBrand;
    color: string;
    postSubmit: (color: string) => void;
}

export default class UpdateBrand extends Component<
    UpdateBrandProps,
    UpdateBrandState
> {
    constructor(props: UpdateBrandProps) {
        super(props);
        this.state = {
            color: props.color,
            error: "",
        };
    }

    forceReauthentication = () => {
        localStorage.removeItem(Storage.ShopID);
        localStorage.removeItem(Storage.IsLoggedIn);
        this.props.auth.signOut();
        this.props.history.push(Path.Login);
    };

    handleChange = (color: string) => {
        this.setState({
            color: color,
        });
    };

    handleSubmit = () => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (shopID == null) {
            return this.setState({
                error: "Shop could not be found. Please navigate back to the shop dashboard to resync",
            });
        }

        if (!isValidHexColor(this.state.color)) {
            return this.setState({
                error: "This colour is not valid. Enter a hex colour code such as #ff8800 or #f80.",
            });
        }

        this.props.auth.currentSession().then(session => {
            this.props.updateBrandAPI.UpdateShopBrand({
                identityToken: session.getIdToken().getJwtToken(),
                shopID: shopID,
                color: this.state.color,
            }).then(response => {
                const statusCode = (response as UpdateShopBrandOutput).statusCode;

                if (statusCode === 403) {
                    return this.forceReauthentication();
                }

                if (statusCode !== 200) {
                    return this.setState({
                        error: "Sorry, we could not update your shop colour at this time. Please try again later."
                    })
                }

                this.props.postSubmit(this.state.color)
            }).catch(err => {
                return this.setState({
                    error: "Sorry, we could not update your shop colour at this time. Please try again later."
                })
            })
        }).catch(err => {
            this.forceReauthentication()
        })
    }

    render() {
        const props: UpdateBrandFormProps = {
            color: this.state.color,
            error: this.state.error,
            onChange: this.handleChange,
            onSubmit: this.handleSubmit,
        };
        return <UpdateBrandForm {...props} />;
    }
}

export interface UpdateBrandFormProps {
    color: string;
    error: string;
    onChange: (color: string) => void;
    onSubmit: () => void;
}

export const UpdateBrandForm: FunctionComponent<UpdateBrandFormProps> = (props) => (
    <Form>
        <Alert variant={"danger"} show={props.error != ""}>
            {props.error}
        </Alert>
        <p>Shop colour is used to brand your shop page for customers.</p>
        <div className="mb-3 d-flex flex-wrap" style={{ gap: "8px" }}>
            {presetColors.map((color) => (
                <button
                    key={color}
                    type="button"
                    aria-label={`Select colour ${color}`}
                    onClick={() => props.onChange(color)}
                    style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: color,
                        border: `1px solid ${Color.Grey}`,
                        borderRadius: "4px",
                        padding: 0,
                    }}
                />
            ))}
        </div>
        <div className="input-group mb-3">
            <div className="input-group-prepend">
                <div className="input-group-text">Colour</div>
            </div>
            <input
                type="color"
                className="form-control form-control-color"
                name={"colour-picker"}
                id={"colour-picker"}
                aria-label="Colour picker"
                value={toPickerValue(props.color)}
                onChange={(e) => props.onChange(e.target.value)}
            />
            <input
                type="text"
                className="form-control"
                name={"colour"}
                id={"colour"}
                placeholder="Enter hex colour"
                value={props.color}
                onChange={(e) => props.onChange(e.target.value)}
            />
        </div>
        <Button style={{ width: "100%" }} buttonvariant={variant.Primary} name={"Submit"} onClick={() => props.onSubmit()} />
    </Form>
)
